/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";
import SingleDayPicker from "@/components/DatePickers/DayPickers/SingleDayPicker/SingleDayPicker";
import InputAdornment from "@mui/material/InputAdornment";

import { colors } from "@dotnet/telematics-design-system";

import { ApiResources } from "../../../../api/resources";
import {
  DATE_FORMATS,
  DriverDataTypes,
  ID_TYPE,
  inputFieldMarginBottom,
  INVALID_DATE,
  TIME_VALUES
} from "../../../../shared";
import { useReleaseFeatureToggle } from "../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../shared/featureToggles/types";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "../../../BannerMessage/BannerMessage";
import InputField from "../../../InputField/InputField";
import QueryDropdown from "../../../QueryDropdown/QueryDropdown";
import ToggleSwitchPaperBox from "../../../ToggleSwitchPaperBox/ToggleSwitchPaperBox";
import {
  disableField,
  toggleSwitchTitle,
  toggleSwitchTooltip
} from "../../functions";
import useScheduleFormContent from "../../hooks/useScheduleFormContent";
import {
  REPEAT_NAME_VALUES,
  SCHEDULE_PAYLOAD_NAMES,
  ScheduleFormContentProps
} from "../../types";
import DownloadDataTypesCheckboxes from "../DownloadDataTypesCheckboxes";
import TimeInput from "./TimeInput/TimeInput";

const ScheduleFormContent = ({
  inputError,
  inputValues,
  repeatValue,
  selectedDataTypes,
  scheduleData,
  startDateTime,
  isGlobalSchedule,
  handleFormChange,
  handleTimeChange,
  validateScheduleName,
  isLoading,
  user,
  createSchedule,
  handleToggleGlobalLinkSwitch,
  isInheritanceEnabled,
  isAttachedToRoot
}: ScheduleFormContentProps) => {
  const { isReleaseFeatureEnabledForRole } = useReleaseFeatureToggle();

  const { t } = useTranslation();

  const {
    initialDatePickerValue,
    findOriginalStartDay,
    updateOriginalStarDate
  } = useScheduleFormContent({
    createSchedule,
    scheduleData,
    inputValues
  });

  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

  const [isRepeatInputOpen, setIsRepeatInputOpen] = useState<boolean>(false);

  const [everyNthDay, setEveryNthDay] = useState<number | null>(null);

  const [originalStartDay, setOriginalStartDay] = useState<number>(
    findOriginalStartDay()
  );

  const hasRepeatValue = repeatValue !== null;

  const isCustomRepeatValue = repeatValue?.value === REPEAT_NAME_VALUES.Custom;

  const isMonthlyRepeatValue =
    repeatValue?.value === REPEAT_NAME_VALUES.Monthly;

  const allowedToToggleGlobalLinking = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.AllowedGlobalScheduleLinking,
    String(user?.role)
  );

  const isInRootCompany = user?.rootCompanySelected;

  const showWarningBanner =
    isGlobalSchedule &&
    isInheritanceEnabled &&
    allowedToToggleGlobalLinking &&
    isInRootCompany;

  const removeToggleSwitchfromUI =
    !isInRootCompany && !scheduleData?.inheritanceEnabled;

  useEffect(() => {
    const positiveRepeatValue = Math.abs(+`${repeatValue?.days}`);

    setEveryNthDay(positiveRepeatValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatValue, originalStartDay]);

  const handleStartDateChange = (value: string | null) => {
    const isInvalidDate = value === INVALID_DATE;

    const newDate = isInvalidDate ? null : value;

    const timeValue = isInvalidDate ? TIME_VALUES.Midnight : startDateTime;

    handleFormChange(
      SCHEDULE_PAYLOAD_NAMES.StartDate,
      `${newDate} ${timeValue ?? ""}`
    );

    updateOriginalStarDate(newDate, setOriginalStartDay);
  };

  const isInputDisabled = disableField(
    isLoading,
    isInRootCompany,
    isAttachedToRoot,
    isInheritanceEnabled
  );

  return (
    <>
      {showWarningBanner && (
        <BannerMessage
          data-testid="toggle-global-schedule-warning--banner"
          status={BANNER_MESSAGE_STATUSES.Warning}
          title={t(`Dialog##toggle global schedule warning banner`)}
          externalCustomStyle={{ margin: "0 0 16px 0" }}
        />
      )}

      {isGlobalSchedule && !removeToggleSwitchfromUI && (
        <ToggleSwitchPaperBox
          title={toggleSwitchTitle(isInRootCompany, t)}
          onToggleSwitch={handleToggleGlobalLinkSwitch}
          isChecked={isInRootCompany ? isInheritanceEnabled : isAttachedToRoot}
          tooltip={toggleSwitchTooltip(isInRootCompany, t)}
          isDisabled={!allowedToToggleGlobalLinking || isLoading}
          customIconStyle={{ marginRight: "12px" }}
        />
      )}

      <InputField
        testId="schedule-form-name-input-field"
        size="medium"
        fullWidth
        required
        readOnly={isGlobalSchedule || isDatePickerOpen}
        errorText={inputError.name ?? ""}
        onBlur={validateScheduleName}
        onChange={event =>
          handleFormChange(SCHEDULE_PAYLOAD_NAMES.Name, event.target.value)
        }
        defaultValue={
          scheduleData ? scheduleData[SCHEDULE_PAYLOAD_NAMES.Name] : undefined
        }
        value={
          isGlobalSchedule && !scheduleData
            ? inputValues[SCHEDULE_PAYLOAD_NAMES.Name]
            : undefined
        }
        labelLeft={t("Dialog##name")}
        placeholder={t("Dialog##enter here")}
        tooltipText={
          isGlobalSchedule ? t("Dialog##field cannot be changed") : undefined
        }
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <div css={css({ display: "flex" })}>
        <QueryDropdown
          data-testid="repeat-period-dropdown"
          resource={ApiResources.Repeat}
          idType={ID_TYPE.Repeat}
          fullWidth
          isRequired
          disableClearable
          size="medium"
          value={repeatValue}
          readOnly={isDatePickerOpen}
          open={isRepeatInputOpen}
          onOpen={() => {
            setIsDatePickerOpen(false);

            !isDatePickerOpen && setIsRepeatInputOpen(true);
          }}
          onClose={() => {
            setIsRepeatInputOpen(false);
          }}
          change={(_, value) =>
            handleFormChange(SCHEDULE_PAYLOAD_NAMES.Repeat, value)
          }
          labelLeft={t("Schedules##repeat")}
          textFieldParams={{
            placeholder: t("Dialog##select")
          }}
          customStyle={{ marginBottom: "16px" }}
          isDisabled={isInputDisabled}
        />

        {isCustomRepeatValue && (
          <InputField
            key={isAttachedToRoot ? "attached-to-root-custom-repeat-field" : ""}
            testId="schedule-form-name-custom-repeat-field"
            size="medium"
            onChange={event =>
              handleFormChange(
                SCHEDULE_PAYLOAD_NAMES.CustomRepeat,
                event.target.value
              )
            }
            readOnly={isLoading}
            disabled={isInputDisabled}
            slotProps={{
              input: {
                inputMode: "numeric",
                endAdornment: inputValues.customRepeat && (
                  <InputAdornment
                    position="end"
                    sx={{
                      ml: 1,
                      "& .MuiTypography-root": {
                        color: colors.textPlaceholder,
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px"
                      }
                    }}
                  >
                    {t("Schedules##days")}
                  </InputAdornment>
                ),
                sx: {
                  "& input": {
                    textAlign: "left",
                    pr: "50px"
                  }
                }
              }
            }}
            value={inputValues.customRepeat}
            placeholder={
              inputValues.customRepeat ? "" : t("Schedules##enter days")
            }
            css={css({
              width: "108px",
              margin: "24px 0 0 12px"
            })}
          />
        )}
      </div>

      {hasRepeatValue && (
        <div
          css={css(inputFieldMarginBottom)}
          data-testid="schedule-date-time-picker"
        >
          <SingleDayPicker
            key={
              isAttachedToRoot
                ? "attached-to-root-date-picker"
                : "not-attached-to-root"
            }
            label={
              createSchedule
                ? t("Schedules##start date and time")
                : t("Schedules##next date and time")
            }
            isRequired
            titleTooltip={t("Dialog##schedule time tooltip description")}
            minDate={moment()}
            originalStartDay={
              isMonthlyRepeatValue ? originalStartDay : undefined
            }
            selectedTime={startDateTime}
            timeInput={
              <TimeInput
                startDateTime={startDateTime}
                handleTimeChange={handleTimeChange}
                isDatePickerOpen={isDatePickerOpen}
                isInputDisabled={isInputDisabled}
                inputError={inputError}
              />
            }
            disabled={isInputDisabled}
            nthDay={everyNthDay}
            externalValue={
              initialDatePickerValue
                ? moment(
                    initialDatePickerValue,
                    DATE_FORMATS.DateHoursMinutes
                  ).toDate()
                : null
            }
            onChange={date =>
              handleStartDateChange(
                date
                  ? moment(date).format(DATE_FORMATS.YearMonthDay)
                  : INVALID_DATE
              )
            }
          />
        </div>
      )}

      {!selectedDataTypes.includes(DriverDataTypes.DriverCard) && (
        <DownloadDataTypesCheckboxes
          selectedDataTypes={selectedDataTypes}
          handleChange={handleFormChange}
          repeatValue={repeatValue}
          isLoading={isInputDisabled}
        />
      )}
    </>
  );
};

export default ScheduleFormContent;
